/**
 * Image Compression Service
 *
 * Compresses images before IndexedDB storage to reduce storage usage.
 * Uses browser-image-compression for client-side compression.
 *
 * Features:
 * - Compresses images above threshold (default: 500KB)
 * - Preserves transparency for PNG images
 * - Configurable quality settings
 * - Tracks compression stats for debugging
 */

import imageCompression from 'browser-image-compression';

// ============================================
// Configuration
// ============================================

/** Default options for image compression */
export interface CompressionOptions {
  /** Maximum file size in MB (default: 0.5 = 500KB) */
  maxSizeMB?: number;
  /** Maximum width/height in pixels (default: 1920) */
  maxWidthOrHeight?: number;
  /** Use WebWorker for compression (default: true) */
  useWebWorker?: boolean;
  /** Preserve EXIF data (default: false) */
  preserveExif?: boolean;
  /** File type to convert to (default: preserves original) */
  fileType?: 'image/jpeg' | 'image/png' | 'image/webp';
}

/** Default compression options */
const DEFAULT_OPTIONS: CompressionOptions = {
  maxSizeMB: 0.5, // 500KB target
  maxWidthOrHeight: 1920, // Reasonable max dimension
  useWebWorker: true,
  preserveExif: false,
};

/** Size threshold below which compression is skipped (bytes) */
const COMPRESSION_THRESHOLD = 500 * 1024; // 500KB

// ============================================
// Compression Statistics
// ============================================

interface CompressionStats {
  originalSize: number;
  compressedSize: number;
  savedBytes: number;
  savedPercent: number;
  wasCompressed: boolean;
}

// ============================================
// Main Functions
// ============================================

/**
 * Compress a Blob if it exceeds the size threshold
 *
 * @param blob - The image blob to compress
 * @param options - Compression options
 * @returns Compressed blob (or original if under threshold)
 *
 * @example
 * ```typescript
 * const compressed = await compressImageBlob(largeImageBlob);
 * console.log(`Reduced from ${blob.size} to ${compressed.size}`);
 * ```
 */
export async function compressImageBlob(
  blob: Blob,
  options: CompressionOptions = {}
): Promise<Blob> {
  // Skip compression for small images
  if (blob.size <= COMPRESSION_THRESHOLD) {
    console.warn(
      `[ImageCompression] Skipping - image already under ${COMPRESSION_THRESHOLD / 1024}KB (${(blob.size / 1024).toFixed(1)}KB)`
    );
    return blob;
  }

  // Skip non-image blobs
  if (!blob.type.startsWith('image/')) {
    console.warn('[ImageCompression] Skipping - not an image blob:', blob.type);
    return blob;
  }

  try {
    const mergedOptions = { ...DEFAULT_OPTIONS, ...options };

    // Convert Blob to File (required by browser-image-compression)
    const file = new File([blob], 'image.png', { type: blob.type });

    console.warn(`[ImageCompression] Compressing ${(blob.size / 1024).toFixed(1)}KB image...`);

    const compressed = await imageCompression(file, {
      maxSizeMB: mergedOptions.maxSizeMB,
      maxWidthOrHeight: mergedOptions.maxWidthOrHeight,
      useWebWorker: mergedOptions.useWebWorker,
      preserveExif: mergedOptions.preserveExif,
      fileType: mergedOptions.fileType,
    });

    const stats = getCompressionStats(blob.size, compressed.size);
    logCompressionStats(stats);

    return compressed;
  } catch (error) {
    console.error('[ImageCompression] Compression failed, using original:', error);
    // Return original blob if compression fails
    return blob;
  }
}

/**
 * Compress multiple image blobs (e.g., front and back designs)
 *
 * @param blobs - Object containing named blobs to compress
 * @param options - Compression options
 * @returns Object with same keys, compressed blobs
 *
 * @example
 * ```typescript
 * const { front, back } = await compressMultipleBlobs({
 *   front: frontBlob,
 *   back: backBlob,
 * });
 * ```
 */
export async function compressMultipleBlobs<T extends Record<string, Blob | null>>(
  blobs: T,
  options: CompressionOptions = {}
): Promise<T> {
  const result = { ...blobs } as T;

  const compressionPromises = Object.entries(blobs).map(async ([key, blob]) => {
    if (blob === null) {
      return { key, blob: null };
    }
    const compressed = await compressImageBlob(blob, options);
    return { key, blob: compressed };
  });

  const compressed = await Promise.all(compressionPromises);

  for (const { key, blob } of compressed) {
    (result as Record<string, Blob | null>)[key] = blob;
  }

  return result;
}

/**
 * Compress a data URL string
 *
 * @param dataUrl - The data URL to compress
 * @param options - Compression options
 * @returns Compressed data URL
 */
export async function compressDataUrl(
  dataUrl: string,
  options: CompressionOptions = {}
): Promise<string> {
  // Validate data URL format
  if (!dataUrl.startsWith('data:image/')) {
    console.warn('[ImageCompression] Invalid data URL format');
    return dataUrl;
  }

  try {
    // Convert data URL to Blob
    const response = await fetch(dataUrl);
    const blob = await response.blob();

    // Compress the blob
    const compressed = await compressImageBlob(blob, options);

    // Convert back to data URL
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          resolve(result);
        } else {
          reject(new Error('Failed to convert blob to data URL'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(compressed);
    });
  } catch (error) {
    console.error('[ImageCompression] Data URL compression failed:', error);
    return dataUrl;
  }
}

// ============================================
// Utility Functions
// ============================================

/**
 * Calculate compression statistics
 */
function getCompressionStats(originalSize: number, compressedSize: number): CompressionStats {
  const savedBytes = originalSize - compressedSize;
  const savedPercent = (savedBytes / originalSize) * 100;

  return {
    originalSize,
    compressedSize,
    savedBytes,
    savedPercent,
    wasCompressed: savedBytes > 0,
  };
}

/**
 * Log compression statistics in a human-readable format
 */
function logCompressionStats(stats: CompressionStats): void {
  if (stats.wasCompressed) {
    console.warn(
      `[ImageCompression] Compressed: ${(stats.originalSize / 1024).toFixed(1)}KB → ${(stats.compressedSize / 1024).toFixed(1)}KB ` +
        `(saved ${stats.savedPercent.toFixed(1)}%)`
    );
  } else {
    console.warn(`[ImageCompression] No reduction: ${(stats.originalSize / 1024).toFixed(1)}KB`);
  }
}

/**
 * Check if compression would be beneficial for a blob
 * Returns true if blob is over threshold
 */
export function shouldCompress(blob: Blob): boolean {
  return blob.size > COMPRESSION_THRESHOLD && blob.type.startsWith('image/');
}

/**
 * Get the compression threshold in bytes
 */
export function getCompressionThreshold(): number {
  return COMPRESSION_THRESHOLD;
}
